/**
 * A texture that every material samples as one object, whatever three clones, and the GL handle it
 * shows: a slot takes another texture's handle, as three's `XREstimatedLight.js:62-70` does, so a
 * switch uploads nothing. A texture every renderer uploads is freed from each when a canvas unmounts.
 */
import { useEffect } from 'react';
import { installedOr } from './shaderPatch/litUniform.js';
/**
 * `cloneUniforms` clones a texture for each material (three r187 `UniformsUtils.js:28-39`). A
 * clone would hold the pixels it was cloned with, or null for a render target's texture
 * (`UniformsUtils.js:32-35`). So a clone of a shared texture is the texture itself.
 *
 * @canonical A texture every material samples as one object
 */
export function sharedTexture(Base) {
    class SharedTexture extends Base {
        clone() {
            return this;
        }
    }
    return SharedTexture;
}
/**
 * The shared `Base` texture a lit uniform `name` samples through, or the one installed under `name`,
 * as after a dev server reloads its module: every program compiled since samples that one.
 *
 * @canonical A texture every material samples as one object
 */
export function installedSlot(name, Base) {
    const Slot = class extends sharedTexture(Base) {
    };
    return installedOr(name, Base, () => new Slot());
}
export function handleOf(renderer, texture) {
    return renderer.properties.get(texture);
}
/** Points `slot` at `texture`'s GL handle on `renderer`. */
export function showInSlot(renderer, slot, texture) {
    handleOf(renderer, slot).__webglTexture = handleOf(renderer, texture).__webglTexture;
}
/** Takes `slot` off `texture` before it is freed: binding a deleted texture is an error. */
export function leaveSlot(renderer, slot, texture) {
    const shown = handleOf(renderer, slot);
    if (shown.__webglTexture === handleOf(renderer, texture).__webglTexture)
        delete shown.__webglTexture;
}
/**
 * Written only by `releasedOnUnmount`, and never cleared. three leaves a dispose listener that closes
 * over each renderer that uploads a texture (r187 `WebGLTextures.js:780`), so a texture that outlives
 * its canvas keeps that renderer reachable until the renderer's own dispose. These stay shared, not
 * per renderer like a pass, as a uniform getter and three's `UniformsLib` read them with no renderer
 * at hand.
 */
const releasedTextures = new Set();
/**
 * `texture`, which each canvas that unmounts frees from every renderer, or a cache that frees its own.
 *
 * @canonical A texture every material samples as one object
 */
export function releasedOnUnmount(texture) {
    releasedTextures.add(texture);
    return texture;
}
/**
 * Frees each `releasedOnUnmount` texture when the canvas it sits in unmounts. A dispose reaches every
 * renderer, so a live canvas would upload each texture again. Both apps show one canvas at a time,
 * so the canvas that follows uploads them once, as it would anyway. The renderer's own dispose frees
 * only its GL copies (r187 `WebGLTextures.js:391-414`), so this still frees each cache that frees its own.
 */
export function SharedTextureRelease() {
    useEffect(() => () => {
        for (const texture of releasedTextures)
            texture.dispose();
    }, []);
    return null;
}
//# sourceMappingURL=sharedTexture.js.map